import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AdminService } from '../../services/admin.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-invite-relative',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './invite-relative.component.html',
  styleUrls: ['./invite-relative.component.css']
})
export class InviteRelativeComponent implements OnInit {
  inviteForm: FormGroup;
  isAdmin = false;
  currentUserReferralCode = '';
  relatives: any[] = [];
  selectedRelative: any = null;
  selectedRelativeIndex: number | null = null;
  isLoadingRelatives = false;
  isSending = false;
  successMessage = '';
  errorMessage = '';
  showInviteCodeInput = false;
  
  // Referral links
  parentReferralLink: string = '';
  spouseReferralLink: string = '';
  siblingReferralLink: string = '';
  baseUrl: string = '';
  profileData: any = null;

  relationshipTypes = [
    { value: 'Parent', label: 'Parent' },
    { value: 'Child', label: 'Child' },
    { value: 'Spouse', label: 'Spouse' },
    { value: 'Sibling', label: 'Sibling' }
  ];

  messageTypes = [
    { value: 'SMS', label: 'SMS', enabled: true },
    { value: 'WhatsApp', label: 'WhatsApp', enabled: false },
    { value: 'Email', label: 'Email', enabled: false }
  ];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private adminService: AdminService,
    private http: HttpClient,
    private router: Router
  ) {
    this.inviteForm = this.fb.group({
      relationshipType: ['', Validators.required],
      inviteCode: ['', Validators.required],
      message: ['', Validators.required],
      messageType: ['SMS', Validators.required],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]]
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.baseUrl = window.location.origin;
    this.loadCurrentUserProfile();
    
    if (this.isAdmin) {
      this.loadRelatives();
      this.inviteForm.get('inviteCode')?.enable();
    } else {
      this.inviteForm.get('inviteCode')?.disable();
    }

    // Watch relationship type changes to update message
    this.inviteForm.get('relationshipType')?.valueChanges.subscribe(() => {
      this.updateMessage();
    });

    // Watch invite code changes to update message
    this.inviteForm.get('inviteCode')?.valueChanges.subscribe(() => {
      this.updateMessage();
    });
  }

  loadCurrentUserProfile(): void {
    this.adminService.getMyProfile().subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          this.profileData = response.data;
          this.currentUserReferralCode = response.data.referralCode || '';
          if (!this.isAdmin) {
            this.inviteForm.patchValue({
              inviteCode: this.currentUserReferralCode
            });
          }
          // Generate referral links for relative login
          if (!this.isAdmin) {
            this.generateReferralLinks(response.data);
          }
          this.updateMessage();
        }
      },
      error: (error) => {
        console.error('Error loading profile:', error);
      }
    });
  }

  loadRelatives(): void {
    this.isLoadingRelatives = true;
    this.adminService.getRelatives(1, 100).subscribe({
      next: (response: any) => {
        this.isLoadingRelatives = false;
        if (response.success && response.data) {
          this.relatives = response.data.relatives || response.relatives || [];
        } else if (response.relatives) {
          this.relatives = response.relatives;
        }
      },
      error: (error) => {
        this.isLoadingRelatives = false;
        console.error('Error loading relatives:', error);
      }
    });
  }

  onRelativeSelect(index: number): void {
    if (index >= 0 && index < this.relatives.length) {
      const relative = this.relatives[index];
      this.selectedRelative = relative;
      this.selectedRelativeIndex = index;
      this.inviteForm.patchValue({
        inviteCode: relative.referralCode
      });
      this.showInviteCodeInput = false;
      this.updateMessage();
    }
  }

  toggleInviteCodeInput(): void {
    this.showInviteCodeInput = !this.showInviteCodeInput;
    if (this.showInviteCodeInput) {
      this.selectedRelative = null;
      this.selectedRelativeIndex = null;
      this.inviteForm.patchValue({
        inviteCode: ''
      });
    }
  }

  updateMessage(): void {
    const relationshipType = this.inviteForm.get('relationshipType')?.value;
    const inviteCode = this.inviteForm.get('inviteCode')?.value;
    
    if (relationshipType && inviteCode) {
      const relationshipLabel = relationshipType.toLowerCase();
      const defaultMessage = `Hi! I'm inviting you to join our family on Familitive as my ${relationshipLabel}. Use my referral code ${inviteCode} to sign up. Join us at ${window.location.origin}/signup?parentReferralCode=${inviteCode}&relationshipType=${relationshipType}`;
      this.inviteForm.patchValue({
        message: defaultMessage
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/home']);
  }

  onSubmit(): void {
    if (this.inviteForm.valid) {
      // Use getRawValue() to include disabled fields
      const formValue = this.inviteForm.getRawValue();
      
      // Only SMS is enabled for now
      if (formValue.messageType !== 'SMS') {
        this.errorMessage = 'Only SMS is currently available. WhatsApp and Email will be available soon.';
        return;
      }

      this.isSending = true;
      this.errorMessage = '';
      this.successMessage = '';

      // Convert to PascalCase to match backend DTO
      const inviteData = {
        RelationshipType: formValue.relationshipType,
        InviteCode: formValue.inviteCode,
        Message: formValue.message,
        MessageType: formValue.messageType,
        MobileNumber: formValue.mobileNumber
      };

      this.http.post<any>(`${environment.apiUrl}/api/relative/send-invite`, inviteData)
        .subscribe({
          next: (response) => {
            this.isSending = false;
            if (response.success) {
              this.successMessage = 'Invitation sent successfully!';
              // Reset form after 2 seconds and navigate back
              setTimeout(() => {
                this.router.navigate(['/home']);
              }, 2000);
            } else {
              this.errorMessage = response.message || 'Failed to send invitation';
            }
          },
          error: (error) => {
            this.isSending = false;
            this.errorMessage = error.error?.message || 'Failed to send invitation. Please try again.';
            console.error('Error sending invite:', error);
          }
        });
    } else {
      this.inviteForm.markAllAsTouched();
    }
  }

  generateReferralLinks(data: any): void {
    // Parent referral link - for adding children (uses own referral code)
    if (data.referralCode) {
      this.parentReferralLink = `${this.baseUrl}/signup?parentReferralCode=${data.referralCode}&parentName=${encodeURIComponent(data.firstName + ' ' + (data.lastName || ''))}`;
    }

    // Spouse referral link - for adding spouse (uses own referral code)
    if (data.referralCode) {
      this.spouseReferralLink = `${this.baseUrl}/signup?spouseReferralCode=${data.referralCode}&spouseName=${encodeURIComponent(data.firstName + ' ' + (data.lastName || ''))}`;
    }

    // Sibling referral link - for adding siblings (uses parent's referral code)
    if (data.parentReferralCode) {
      this.siblingReferralLink = `${this.baseUrl}/signup?parentReferralCode=${data.parentReferralCode}`;
    } else {
      this.siblingReferralLink = ''; // No parent, so no sibling link available
    }
  }

  copyToClipboard(text: string, type: string): void {
    if (!text) {
      this.errorMessage = `${type} referral link is not available`;
      return;
    }
    
    navigator.clipboard.writeText(text).then(() => {
      this.successMessage = `${type} referral link copied to clipboard!`;
      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    }).catch(err => {
      this.errorMessage = 'Failed to copy link. Please copy manually.';
      console.error('Failed to copy:', err);
    });
  }

  shareLink(type: string): void {
    let link = '';
    let title = '';
    let text = '';

    switch (type) {
      case 'parent':
        link = this.parentReferralLink;
        title = 'Add me as your parent on Familitive';
        text = `Join my family tree on Familitive! Use this link to register and connect with me: ${link}`;
        break;
      case 'spouse':
        link = this.spouseReferralLink;
        title = 'Join me on Familitive as my spouse';
        text = `Join my family tree on Familitive! Use this link to register and connect as my spouse: ${link}`;
        break;
      case 'sibling':
        link = this.siblingReferralLink;
        title = 'Join me as my sibling on Familitive';
        text = `Join my family tree on Familitive! Use this link to register and connect as my sibling: ${link}`;
        break;
    }

    if (!link) {
      this.errorMessage = `${type} referral link is not available`;
      return;
    }

    // Use Web Share API if available, otherwise fallback to clipboard
    if (navigator.share) {
      navigator.share({
        title: title,
        text: text,
        url: link
      }).then(() => {
        this.successMessage = `Shared ${type} referral link successfully!`;
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      }).catch(err => {
        // User cancelled or error - fallback to copy
        this.copyToClipboard(link, type);
      });
    } else {
      this.copyToClipboard(link, type);
    }
  }
}

